// Componente Profile - Tela de Perfil redesenhada com estilo Nutrição/Treinos
import { useState, useEffect, FormEvent, useRef, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useProfile } from '../contexts/ProfileContext';
import { supabase } from '../../lib/supabaseClient';
import { User, Calendar, LogOut, Save, Camera, ChevronRight, RefreshCw, Shield } from 'lucide-react';

// Cores e constantes do design system (mesmas da Nutrição)
const ACCENT_RED = '#E53935';
const BORDER_COLOR = 'rgba(255, 255, 255, 0.1)';
const BG_DARK = '#0D0D0D';


interface Profile {
  id: string;
  full_name: string | null;
  avatar_url: string | null;
  created_at: string;
}

interface ProfileProps {
  onRedoOnboarding?: () => void;
}

export default function Profile({ onRedoOnboarding }: ProfileProps) {
  const { user, signOut, refreshUser } = useAuth();
  const { refreshProfile } = useProfile();

  // Forçar atualização da foto no dashboard
  const forceDashboardUpdate = () => {
    // Disparar evento customizado para atualizar dashboard
    window.dispatchEvent(new CustomEvent('profileUpdated'));
  };
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [fullName, setFullName] = useState('');
  const [avatarUrl, setAvatarUrl] = useState<string | null>(null);
  const [selectedImage, setSelectedImage] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const loadProfile = useCallback(async () => {
    if (!user) return;

    const { data } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', user.id)
      .maybeSingle();

    if (data) {
      setProfile(data);
      setFullName(data.full_name || '');
      setAvatarUrl(data.avatar_url || null);
    }

    setLoading(false);
  }, [user]);

  useEffect(() => {
    loadProfile();
  }, [user, loadProfile]);

  const handleImageSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setSelectedImage(file);
      const reader = new FileReader();
      reader.onload = (event) => {
        const imageUrl = event.target?.result as string;
        setAvatarUrl(imageUrl);
        // Atualizar contexto imediatamente para preview
        window.dispatchEvent(new CustomEvent('profileUpdated'));
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSave = async (e: FormEvent) => {
    e.preventDefault();
    if (!user) return;

    setSaving(true);

    let finalAvatarUrl = avatarUrl;

    // Se houver uma nova imagem selecionada, fazer upload
    if (selectedImage) {
      const fileExt = selectedImage.name.split('.').pop();
      const fileName = `${user.id}-${Date.now()}.${fileExt}`;
      const filePath = `avatars/${fileName}`;

      const { error: uploadError } = await supabase.storage
        .from('avatars')
        .upload(filePath, selectedImage);

      if (uploadError) {
        console.error('Erro ao fazer upload da imagem:', uploadError);
      } else {
        const { data: { publicUrl } } = supabase.storage
          .from('avatars')
          .getPublicUrl(filePath);
        finalAvatarUrl = publicUrl;
      }
    }

    await supabase.from('profiles').upsert({
      id: user.id,
      full_name: fullName || null,
      avatar_url: finalAvatarUrl,
    });

    setSelectedImage(null);
    await loadProfile();
    await refreshUser(); // Atualizar o contexto de autenticação
    await refreshProfile(); // Atualizar o contexto do perfil
    forceDashboardUpdate(); // Forçar atualização do dashboard
    setSaving(false);
  };

  const formatMemberSince = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('pt-BR', {
      month: 'long',
      year: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: BG_DARK }}>
        <div className="text-center">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#E53935] mx-auto mb-4"></div>
          <p className="text-[#A1A1A1] text-sm tracking-wide">Carregando perfil...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pb-24" style={{ backgroundColor: BG_DARK }}>
      <div className="container mx-auto px-4 pt-6 max-w-md">
        <header className="mb-8 pt-4">
          <h1 className="text-xl font-extrabold text-white mb-2" style={{ fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif', fontWeight: 800 }}>
            Meu Perfil
          </h1>
          <p className="text-[#A1A1A1] text-sm">Gerencie suas informações pessoais</p>
        </header>

        <div className="space-y-6">
          {/* Foto de Perfil */}
          <div className="rounded-[18px] p-6 bg-[#141414] border shadow-lg relative overflow-hidden" style={{ borderColor: BORDER_COLOR }}>
            <div className="absolute top-0 right-0 p-6 opacity-5 pointer-events-none">
              <User className="w-32 h-32 text-white" />
            </div>

            <div className="relative z-10 flex flex-col items-center">
              <div className="relative mb-4 group">
                <div className="w-28 h-28 rounded-full overflow-hidden border-2 shadow-2xl" style={{ borderColor: ACCENT_RED }}>
                  {avatarUrl ? (
                    <img
                      src={avatarUrl}
                      alt="Foto de perfil"
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                    />
                  ) : (
                    <div className="w-full h-full bg-[#1A1A1A] flex items-center justify-center text-white text-3xl font-bold">
                      {fullName ? fullName.charAt(0).toUpperCase() : 'U'}
                    </div>
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  className="absolute bottom-0 right-0 p-2.5 rounded-full transition-all duration-200 hover:scale-110 shadow-lg border border-[#141414]"
                  style={{ backgroundColor: ACCENT_RED }}
                >
                  <Camera className="w-4 h-4 text-white" />
                </button>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleImageSelect}
                  className="hidden"
                />
              </div>
              <p className="text-[#A1A1A1] text-xs font-medium">Toque na câmera para alterar</p>
            </div>
          </div>

          {/* Informações do Perfil */}
          <form onSubmit={handleSave} className="space-y-6">
            <div className="rounded-[18px] p-6 bg-[#141414] border shadow-lg" style={{ borderColor: BORDER_COLOR }}>
              <div className="flex items-center gap-3 mb-6">
                <div className="p-2 rounded-lg bg-[#E53935]/10 border border-[#E53935]/20">
                  <Shield className="w-5 h-5 text-[#E53935]" />
                </div>
                <h2 className="text-white font-bold text-base">Informações Pessoais</h2>
              </div>

              <div className="space-y-5">
                <div>
                  <label className="block text-[#A1A1A1] text-xs font-bold uppercase tracking-wider mb-2 ml-1">
                    Nome
                  </label>
                  <input
                    type="text"
                    value={fullName}
                    onChange={(e) => setFullName(e.target.value)}
                    className="w-full px-4 py-3.5 bg-[#0D0D0D] text-white text-sm rounded-xl border focus:outline-none focus:border-[#E53935] transition-colors"
                    style={{ borderColor: BORDER_COLOR }}
                    placeholder="Seu nome"
                  />
                </div>

                {/* Membro desde */}
                <div className="pt-5 border-t border-white/5">
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-[#1A1A1A] border border-white/5">
                      <Calendar className="w-4 h-4 text-[#A1A1A1]" />
                    </div>
                    <div>
                      <h3 className="text-white font-medium text-sm">Membro desde</h3>
                      <p className="text-[#A1A1A1] text-xs mt-0.5">
                        {profile?.created_at ? formatMemberSince(profile.created_at) : 'Carregando...'}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Botões de Ação */}
            <div className="space-y-3">
              <button
                type="button"
                onClick={onRedoOnboarding}
                className="w-full p-4 rounded-xl border transition-all duration-200 hover:scale-[1.02] text-left group flex items-center justify-between"
                style={{
                  backgroundColor: 'rgba(229, 57, 53, 0.05)',
                  borderColor: 'rgba(229, 57, 53, 0.2)',
                }}
              >
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-[#E53935]/10 text-[#E53935]">
                    <RefreshCw className="w-5 h-5" />
                  </div>
                  <span className="text-white font-bold text-sm group-hover:text-[#E53935] transition-colors">Atualizar questionário</span>
                </div>
                <ChevronRight className="w-5 h-5 text-[#A1A1A1] group-hover:text-[#E53935] transition-colors" />
              </button>

              <button
                type="button"
                onClick={signOut}
                className="w-full p-4 rounded-xl border transition-all duration-200 hover:scale-[1.02] text-left group flex items-center justify-between"
                style={{
                  backgroundColor: 'rgba(255, 255, 255, 0.03)',
                  borderColor: BORDER_COLOR,
                }}
              >
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-white/5 text-[#A1A1A1] group-hover:text-white transition-colors">
                    <LogOut className="w-5 h-5" />
                  </div>
                  <span className="text-[#A1A1A1] font-medium text-sm group-hover:text-white transition-colors">Sair da conta</span>
                </div>
                <ChevronRight className="w-5 h-5 text-[#A1A1A1] group-hover:text-white transition-colors" />
              </button>
            </div>

            {/* Botão Salvar */}
            <div className="pt-2">
              <button
                type="submit"
                disabled={saving}
                className="w-full py-4 bg-[#E53935] hover:bg-[#D32F2F] text-white font-bold rounded-xl transition-all duration-200 hover:shadow-lg hover:shadow-red-900/20 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {saving ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    Salvando...
                  </>
                ) : (
                  <>
                    <Save className="w-5 h-5" />
                    Salvar alterações
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
